
import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { MentionDropdown } from './MentionDropdown';
import { useUserMentions, MentionUser } from '@/hooks/useUserMentions';

interface MentionTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
  minHeight?: number;
}

export const MentionTextArea = ({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  maxLength,
  minHeight = 80
}: MentionTextAreaProps) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { users, searchUsers } = useUserMentions();

  // Search users when mention query changes
  useEffect(() => {
    if (mentionQuery) {
      searchUsers(mentionQuery);
    }
  }, [mentionQuery, searchUsers]);

  // Reset selected index when users change
  useEffect(() => {
    setSelectedMentionIndex(0);
  }, [users]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Check for @ mention
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions && users.length > 0) {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedMentionIndex(prev => 
            prev > 0 ? prev - 1 : users.length - 1
          );
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedMentionIndex(prev => 
            prev < users.length - 1 ? prev + 1 : 0
          );
          break;
        case 'Enter':
        case 'Tab':
          if (users[selectedMentionIndex]) {
            e.preventDefault();
            insertMention(users[selectedMentionIndex]);
          }
          break;
        case 'Escape':
          setShowMentions(false);
          break;
      }
    }
  };

  const insertMention = (user: MentionUser) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    
    // Find the start of the @ mention
    const mentionStart = textBeforeCursor.lastIndexOf('@');
    const textBeforeMention = textBeforeCursor.slice(0, mentionStart);
    
    const mentionText = `@${user.username}`;
    const newValue = textBeforeMention + mentionText + ' ' + textAfterCursor;
    
    onChange(newValue);
    setShowMentions(false);
    
    // Set cursor position after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = textBeforeMention.length + mentionText.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleSelectMention = (user: MentionUser) => {
    insertMention(user);
  };

  // Render text with highlighted mentions
  const renderValueWithMentions = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    return text.replace(mentionRegex, '<span class="text-blue-600 font-medium">@$1</span>');
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        maxLength={maxLength}
        style={{ minHeight: `${minHeight}px` }}
      />
      
      <MentionDropdown
        users={users}
        selectedIndex={selectedMentionIndex}
        onSelect={handleSelectMention}
        visible={showMentions}
      />
      
      {/* Helper text */}
      {showMentions && users.length === 0 && mentionQuery && (
        <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 mb-1 text-sm text-gray-500">
          No users found matching "@{mentionQuery}"
        </div>
      )}
    </div>
  );
};
